import React, { useMemo, useRef, useCallback } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { AutoSizer, List, InfiniteLoader } from 'react-virtualized';
import { KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material';
import DeviceCard from '../DeviceCard';
import { DeviceInfo } from '../types';

interface DevicesGroupedListProps {
  devices: DeviceInfo[];
  filter: string;
  selectedLabels: string[];
  selectedModems: string[];
  labelFilterMode: 'any' | 'all' | 'exact' | 'exclude';
  expandedGroups: string[];
  onToggleGroup: (group: string) => void;
  selectionMode: boolean;
  selectedDevices: string[];
  onGetSelectionHandler: (box: string) => () => void;
  onDevicesUpdated: () => void;
}

const DevicesGroupedList: React.FC<DevicesGroupedListProps> = ({
  devices,
  filter,
  selectedLabels,
  selectedModems,
  labelFilterMode,
  expandedGroups,
  onToggleGroup,
  selectionMode,
  selectedDevices,
  onGetSelectionHandler,
  onDevicesUpdated,
}) => {
  const theme = useTheme();
  const listRefs = useRef<{ [key: string]: List | null }>({});

  const isRowLoaded = useCallback(({ index, data }: { index: number; data: DeviceInfo[] }) => index < data.length, []);
  const loadMoreRows = useCallback(({ startIndex, stopIndex }: { startIndex: number; stopIndex: number }) => Promise.resolve(), []);

  const groupedDevices = useMemo(() => {
    const groups: { [key: string]: DeviceInfo[] } = {};
    for (const device of devices) {
      // Check if device is from service
      if (device.status === 'SERWIS') {
        if (!groups['Serwis']) groups['Serwis'] = [];
        groups['Serwis'].push(device);
      } else {
        // For inventory devices, group by operator
        const operator = device.operator || 'Unknown';
        if (!groups[operator]) groups[operator] = [];
        groups[operator].push(device);
      }
    }
    return groups;
  }, [devices]);

  const effectiveExpandedGroups = expandedGroups;

  const sortDevicesPreferred = useCallback((list: DeviceInfo[]) => {
    const now = Date.now();
    const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
    const hasSim = (d: DeviceInfo) => {
      const iccid = typeof d.iccid === 'string' ? d.iccid.trim() : '';
      const modem = typeof d.modem === 'string' ? d.modem.trim() : '';
      return (iccid && iccid.length > 0) || (modem && modem.length > 0);
    };
    const isStale = (d: DeviceInfo) => {
      if (!d.time_stamp) return true; // brak odczytu = na koniec
      const t = new Date(d.time_stamp).getTime();
      if (Number.isNaN(t)) return true;
      return now - t > sevenDaysMs; // stare odczyty na koniec
    };
    const numBox = (d: DeviceInfo) => {
      const n = parseInt(String(d.box), 10);
      return Number.isNaN(n) ? Number.MAX_SAFE_INTEGER : n;
    };
    return [...list].sort((a, b) => {
      // 1) Stare odczyty na koniec
      const sa = isStale(a) ? 1 : 0;
      const sb = isStale(b) ? 1 : 0;
      if (sa !== sb) return sa - sb;
      // 2) Urządzenia z odczytaną SIM przed tymi bez
      const ha = hasSim(a) ? 0 : 1;
      const hb = hasSim(b) ? 0 : 1;
      if (ha !== hb) return ha - hb;
      // 3) Dla tych z odczytaną SIM sortuj po numerze box
      if (ha === 0 && hb === 0) {
        return numBox(a) - numBox(b);
      }
      // 4) Fallback: też po numerze box
      return numBox(a) - numBox(b);
    });
  }, []);

  const renderGroupHeader = useCallback(
    (operator: string, count: number, isExpanded: boolean) => (
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 1.5,
          mt: 2,
          cursor: 'pointer',
          borderRadius: 1,
          backgroundColor: alpha(theme.palette.background.paper, 0.7),
          border: `1px solid ${theme.palette.borders?.primary || theme.palette.divider}`,
        }}
        onClick={() => onToggleGroup(operator)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ mr: 1 }}>
            {isExpanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
          </Box>
          <Typography variant="h6">
            {operator} ({count})
          </Typography>
        </Box>
      </Paper>
    ),
    [onToggleGroup, theme]
  );

  const operatorOrder = ['Orange', 'Plus', 'Play', 'Unknown', 'Serwis'];

  return (
    <>{operatorOrder.concat(Object.keys(groupedDevices).filter((g) => !operatorOrder.includes(g))).map((operator) => {
      const list = groupedDevices[operator] || [];
      const sorted = sortDevicesPreferred(list);
      if (list.length === 0) return null;
      const isExpanded = effectiveExpandedGroups.includes(operator);
      return (
        <Box key={operator} sx={{ mb: 1 }}>
          {renderGroupHeader(operator, list.length, isExpanded)}
          {isExpanded && (
            <Box
              sx={{
                mt: 1,
                ml: 4,
                height: Math.min(600, Math.ceil(list.length / 4) * 130),
                // Container uses default background; items use paper
                backgroundColor: alpha(theme.palette.background.default, 0.8),
                borderRadius: 1,
                
                overflow: 'hidden',
                position: 'relative',
                boxShadow: `0 0 10px ${alpha(theme.palette.divider, 0.1)}`,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '2px',
                  background: `linear-gradient(to right, ${alpha(theme.palette.divider, 0.2)}, ${alpha(
                    theme.palette.divider,
                    0.3
                  )})`,
                },
              }}
            >
              <AutoSizer>
                {({ width, height }) => {
                  const itemWidth = 190;
                  const itemsPerRow = Math.max(Math.floor(width / itemWidth) || 1, 1);
                  const rowCount = Math.ceil(sorted.length / itemsPerRow);
                  return sorted.length > 100 ? (
                    <InfiniteLoader
                      isRowLoaded={({ index }) => isRowLoaded({ index, data: sorted })}
                      loadMoreRows={({ startIndex, stopIndex }) => loadMoreRows({ startIndex, stopIndex })}
                      rowCount={rowCount}
                      threshold={10}
                    >
                      {({ onRowsRendered, registerChild }) => (
                        <List
                          ref={(ref) => {
                            registerChild(ref);
                            listRefs.current[operator] = ref;
                          }}
                          width={width}
                          height={height}
                          rowCount={rowCount}
                          rowHeight={130}
                          overscanRowCount={5}
                          onRowsRendered={onRowsRendered}
                          rowRenderer={({ index, key, style }) => {
                            const items: React.ReactNode[] = [];
                            const fromIndex = index * itemsPerRow;
                            const toIndex = Math.min(fromIndex + itemsPerRow, sorted.length);
                            for (let i = fromIndex; i < toIndex; i++) {
                              const device = sorted[i];
                              items.push(
                                <Box key={device.box} style={{ display: 'inline-block', padding: '5px' }}>
                                  <DeviceCard
                                    device={device}
                                    selectionMode={selectionMode}
                                    isSelected={selectedDevices.includes(device.box)}
                                    onSelect={onGetSelectionHandler(device.box)}
                                    onDevicesUpdated={onDevicesUpdated}
                                  />
                                </Box>
                              );
                            }
                            return (
                              <div key={key} style={style}>
                                {items}
                              </div>
                            );
                          }}
                        />
                      )}
                    </InfiniteLoader>
                  ) : (
                    <List
                      ref={(ref) => (listRefs.current[operator] = ref)}
                      width={width}
                      height={height}
                      rowCount={rowCount}
                      rowHeight={130}
                      overscanRowCount={5}
                      rowRenderer={({ index, key, style }) => {
                        const items: React.ReactNode[] = [];
                        const fromIndex = index * itemsPerRow;
                        const toIndex = Math.min(fromIndex + itemsPerRow, sorted.length);
                        for (let i = fromIndex; i < toIndex; i++) {
                          const device = sorted[i];
                          items.push(
                            <Box key={device.box} style={{ display: 'inline-block', padding: '5px' }}>
                              <DeviceCard
                                device={device}
                                selectionMode={selectionMode}
                                isSelected={selectedDevices.includes(device.box)}
                                onSelect={onGetSelectionHandler(device.box)}
                                onDevicesUpdated={onDevicesUpdated}
                              />
                            </Box>
                          );
                        }
                        return (
                          <div key={key} style={style}>
                            {items}
                          </div>
                        );
                      }}
                    />
                  );
                }}
              </AutoSizer>
            </Box>
          )}
        </Box>
      );
    })}</>
  );
};

export default DevicesGroupedList;


